/**
 * OverlayCard — Liquid glass overlay cards for text, images, and bullet lists.
 *
 * Positioned above the avatar, stacked with spacing.
 * Auto-dismiss after timeout. Spring-animated enter/exit.
 */

import { createGlassPanel, glassText, createGlassButton } from '../glass'
import { springEnter, springExit, type SpringAnimation } from '../spring'
import { injectTokens, tokens } from '../tokens'

/** Auto-dismiss after 15 seconds. */
const AUTO_DISMISS_MS = 15_000
/** Max width of overlay cards. */
const MAX_WIDTH = 320

interface ActiveCard {
  el: HTMLElement
  timer: ReturnType<typeof setTimeout>
  anim: SpringAnimation | null
}

export class GlassOverlayRenderer {
  private cards: ActiveCard[] = []
  private anchor: HTMLElement | null = null

  /** Set the anchor element that cards stack above. */
  setAnchor(el: HTMLElement): void {
    this.anchor = el
  }

  /** Show a text card. */
  showTextCard(title: string, body: string): void {
    const card = this.createCard()

    const titleEl = document.createElement('div')
    glassText(titleEl, 'primary', 'base', 'semibold')
    titleEl.textContent = title
    titleEl.style.marginBottom = 'var(--hs-space-2)'
    card.el.appendChild(titleEl)

    const bodyEl = document.createElement('div')
    glassText(bodyEl, 'secondary', 'sm', 'normal')
    bodyEl.textContent = body
    bodyEl.style.whiteSpace = 'pre-wrap'
    card.el.appendChild(bodyEl)

    this.mountCard(card)
  }

  /** Show an image card. */
  showImageCard(imageUrl: string, caption?: string): void {
    const card = this.createCard()

    const img = document.createElement('img')
    img.src = imageUrl
    img.alt = caption || ''
    Object.assign(img.style, {
      width: '100%',
      borderRadius: 'var(--hs-radius-sm)',
      display: 'block',
      marginBottom: caption ? 'var(--hs-space-2)' : '0',
    })
    card.el.appendChild(img)

    if (caption) {
      const cap = document.createElement('div')
      glassText(cap, 'secondary', 'sm', 'normal')
      cap.textContent = caption
      card.el.appendChild(cap)
    }

    this.mountCard(card)
  }

  /** Show a bullet list card. */
  showBulletList(title: string, items: string[]): void {
    const card = this.createCard()

    const titleEl = document.createElement('div')
    glassText(titleEl, 'primary', 'base', 'semibold')
    titleEl.textContent = title
    titleEl.style.marginBottom = 'var(--hs-space-2)'
    card.el.appendChild(titleEl)

    const list = document.createElement('ul')
    Object.assign(list.style, {
      margin: '0',
      paddingLeft: 'var(--hs-space-5)',
      listStyleType: 'none',
    })

    items.forEach(text => {
      const li = document.createElement('li')
      Object.assign(li.style, {
        marginBottom: 'var(--hs-space-1)',
        position: 'relative',
        paddingLeft: '0',
      })

      // Custom bullet (accent colored dot)
      const bullet = document.createElement('span')
      Object.assign(bullet.style, {
        position: 'absolute',
        left: '-16px',
        top: '6px',
        width: '5px',
        height: '5px',
        borderRadius: '50%',
        background: 'var(--hs-accent)',
      })
      li.appendChild(bullet)

      const span = document.createElement('span')
      glassText(span, 'secondary', 'sm', 'normal')
      span.textContent = text
      li.appendChild(span)

      list.appendChild(li)
    })

    card.el.appendChild(list)
    this.mountCard(card)
  }

  /** Dismiss all visible cards. */
  dismissAll(): void {
    this.cards.forEach(card => this.removeCard(card))
    this.cards = []
  }

  // -----------------------------------------------------------------------
  // Internal
  // -----------------------------------------------------------------------

  private createCard(): ActiveCard {
    injectTokens()

    const el = createGlassPanel({
      radius: 'md',
      elevation: 'elevated',
      className: 'hs-overlay-card',
    })

    Object.assign(el.style, {
      position: 'fixed',
      maxWidth: `${Math.min(MAX_WIDTH, window.innerWidth - 32)}px`,
      padding: 'var(--hs-space-4)',
      zIndex: tokens.zOverlay,
      opacity: '0',
      pointerEvents: 'auto',
    })

    // Close button
    const closeBtn = createGlassButton({
      label: '\u2715',
      variant: 'ghost',
      size: 'sm',
      onClick: () => this.dismiss(card),
    })
    Object.assign(closeBtn.style, {
      position: 'absolute',
      top: 'var(--hs-space-2)',
      right: 'var(--hs-space-2)',
      minWidth: '0',
      padding: '2px 6px',
      fontSize: 'var(--hs-text-xs)',
    })
    el.appendChild(closeBtn)

    const timer = setTimeout(() => this.dismiss(card), AUTO_DISMISS_MS)
    const card: ActiveCard = { el, timer, anim: null }
    return card
  }

  private mountCard(card: ActiveCard): void {
    document.body.appendChild(card.el)
    this.positionCard(card.el, this.cards.length)
    this.cards.push(card)

    card.anim = springEnter(card.el, { preset: 'snappy' })
  }

  private positionCard(el: HTMLElement, stackIndex: number): void {
    // Stack above the avatar area, bottom-center
    const bottomOffset = 500 + stackIndex * 10 // base offset + stack spacing
    const cardHeight = el.offsetHeight || 80

    Object.assign(el.style, {
      bottom: `${bottomOffset + stackIndex * (cardHeight + 8)}px`,
      left: '50%',
      transform: 'translateX(-50%)',
    })
  }

  private dismiss(card: ActiveCard): void {
    const idx = this.cards.indexOf(card)
    if (idx >= 0) this.cards.splice(idx, 1)
    this.removeCard(card)
  }

  private removeCard(card: ActiveCard): void {
    clearTimeout(card.timer)
    card.anim?.stop()
    card.anim = springExit(card.el, { remove: true, preset: 'smooth' })
  }
}
